<template>
  <StaffLayout section="Roles Master" showHeader>
    <div v-if="isLoading"><Loading /></div>
    <div v-else>
      <div v-if="roles && roles.length > 0">
        <AddButton @click="addClick">Add Role Type</AddButton>
        <div v-if="roles.filter((r) => r.isPinned).length > 0" class="mt-2">
          Pinned roles:
          <div
            class="px-4 py-2 my-2 bg-gray-100 border-t cursor-pointer hover:bg-blue-200"
            :class="{ 'bg-gray-200': role.isStaffRole }"
            v-for="role in roles.filter((r) => r.isPinned)"
            :key="role.roleTypeId"
            @click="editClick(role)"
          >
            {{ role.name }}
            <span v-if="role.isStaffRole" class="text-gray-500"> (Staff)</span>
          </div>
        </div>
        <div class="mt-8">
          Unpinned roles:
          <div
            class="px-4 py-2 my-2 bg-gray-100 border-t cursor-pointer hover:bg-blue-200"
            :class="{ 'bg-gray-200': role.isStaffRole }"
            v-for="role in roles.filter((r) => !r.isPinned)"
            :key="role.roleTypeId"
            @click="editClick(role)"
          >
            {{ role.name }}
            <span v-if="role.isStaffRole" class="text-gray-500"> (Staff)</span>
          </div>
        </div>
      </div>
      <div v-else class="mb-2">-- No Roles found --</div>
      <AddButton @click="addClick">Add Role Type</AddButton>
      <EditPopup
        entity-name="Role"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="saveError"
        :delete-enabled="isEditing"
        @close="isPopupVisible = false"
        @save="saveClick"
        @delete="deleteClick"
      >
        <BaseInput label="Role Name" v-model="name" type="text" :error="errors.name" />
        <BaseInput
          label="System Name"
          v-model="systemName"
          type="text"
          :error="errors.systemName"
        />
        <div class="mt-4">
          <BaseCheckbox label="Is Staff Role" v-model="isStaffRole" />
          <BaseCheckbox label="Is Pinned" v-model="isPinned" />
        </div>
      </EditPopup>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import { RoleTypeDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

let isLoading = ref(true);
const adminStore = useAdminStore();

const isPopupVisible = ref(false);
const isEditing = ref(false);
let saveError = ref("");

// Editing values

let roles = computed(() => adminStore.roleTypes);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    name: yup.string().required().max(50).label("Role Name"),
    systemName: yup.string().required().min(3).max(50).label("System Name"),
  }),
  initialValues: {
    name: "",
    systemName: "",
  },
});

let roleTypeId = ref(-1);
const { value: name } = useField<string>("name");
const { value: systemName } = useField<string>("systemName");
let isStaffRole = ref(false);
let isPinned = ref(false);

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveRole = {
    roleTypeId: roleTypeId.value,
    name: name.value,
    systemName: systemName.value,
    isStaffRole: isStaffRole.value,
    isPinned: isPinned.value,
  } as RoleTypeDTO;

  console.log("saving role", saveRole);
  saveError.value = await adminStore.saveRoleType(saveRole);
  if (!saveError.value) {
    isPopupVisible.value = false;
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

onMounted(async () => {
  console.log("Master Role list Page mounted");
  isLoading.value = true;
  await adminStore.getRoleTypes();
  isLoading.value = false;
});

function editClick(role: RoleTypeDTO) {
  isEditing.value = true;
  saveError.value = "";

  roleTypeId.value = role.roleTypeId ?? 0;
  name.value = role.name ?? "";
  systemName.value = role.systemName ?? "";
  isStaffRole.value = role.isStaffRole ?? false;
  isPinned.value = role.isPinned;

  isPopupVisible.value = true;
}

function addClick() {
  isEditing.value = false;
  saveError.value = "";

  roleTypeId.value = 0;
  name.value = "";
  systemName.value = "";
  isStaffRole.value = false;
  isPinned.value = false;

  isPopupVisible.value = true;
}

async function deleteClick() {
  saveError.value = await adminStore.deleteRoleType(roleTypeId.value);

  if (saveError.value) return;
  await adminStore.getRoleTypes(true);
  isPopupVisible.value = false;
}
</script>
